import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import blogService, { BlogPost } from '../services/blogService';
import { setStructuredData, updateCanonicalLink, updateMetaTag } from '../utils/seoHelpers';
import './FloristBlog.css';

const META_FALLBACK =
  'Floristbloggen från Lillaflora bjuder på mysiga dukningar, dofter och tips direkt från butiken i Kramfors.';

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const estimateReadingTime = (html: string) => {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 2;
  const words = text.split(' ').length;
  return Math.max(2, Math.round(words / 180));
};

const FloristBlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [suggestions, setSuggestions] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return () => {
      setStructuredData('floristbloggen-post');
    };
  }, [slug]);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setError('Bloggposten hittades inte.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await blogService.getBySlug(slug);
        if (!data) {
          setError('Bloggposten hittades inte.');
          setPost(null);
        } else {
          setPost(data);
        }
      } catch (err) {
        console.error(err);
        setError('Det gick inte att ladda bloggposten just nu.');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const data = await blogService.getPublished(4);
        setSuggestions(data.filter((item) => item.slug !== slug).slice(0, 3));
      } catch (err) {
        console.warn('Kunde inte hämta relaterade artiklar', err);
      }
    };

    fetchSuggestions();
  }, [slug]);

  useEffect(() => {
    if (!post) {
      setStructuredData('floristbloggen-post');
      return;
    }

    const seoTitle = post.seo_title || `${post.title} | Floristbloggen – Lillaflora`;
    const seoDescription = post.seo_description || post.short_description || META_FALLBACK;
    const seoKeywords = post.seo_keywords || 'floristblogg, lillaflora, dukning, inspiration';
    const canonical = `https://lillaflora.nu/floristbloggen/${post.slug}`;

    document.title = seoTitle;
    updateMetaTag('description', seoDescription);
    updateMetaTag('keywords', seoKeywords);
    updateCanonicalLink(canonical);

    const structured = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: seoDescription,
      image: post.hero_image || undefined,
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at,
      author: {
        '@type': 'Person',
        name: 'Floristteamet på Lillaflora'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Lillaflora',
        logo: {
          '@type': 'ImageObject',
          url: 'https://lillaflora.nu/favicon.ico'
        }
      },
      mainEntityOfPage: canonical,
      articleBody: (post.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    };

    setStructuredData('floristbloggen-post', structured);
  }, [post]);

  const readingTime = useMemo(() => estimateReadingTime(post?.content || ''), [post?.content]);

  if (loading) {
    return (
      <div className="florist-blog-page">
        <div className="blog-state container">
          <div className="blog-spinner" />
          <p>Floristen fluffar kuddarna...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="florist-blog-page">
        <div className="blog-state blog-error container">
          <h1>Hoppsan!</h1>
          <p>{error || 'Inlägget verkar ha flyttat på sig.'}</p>
          <Link to="/floristbloggen" className="blog-cta-primary">
            Tillbaka till Floristbloggen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="florist-blog-page">
      <section className="blog-article-hero container">
        <p className="blog-tag">Floristbloggen</p>
        <h1>{post.title}</h1>
        {post.short_description && <p className="blog-hero-lead">{post.short_description}</p>}
        <div className="blog-meta">
          <span>{formatDate(post.published_at || post.created_at)}</span>
          <span>{readingTime} min lästid</span>
        </div>
      </section>

      <article className="blog-post container">
        {post.hero_image ? (
          <img src={post.hero_image} alt={post.title} className="hero" loading="lazy" />
        ) : (
          <div className="blog-featured-placeholder hero" aria-hidden="true">
            Floristbloggen
          </div>
        )}
        <div className="content" dangerouslySetInnerHTML={{ __html: post.content }} />

        <div className="blog-cta-panel">
          <h3>Vill du skapa samma känsla hemma?</h3>
          <p>Vi hjälper dig gärna att plocka ihop buketter, dofter och detaljer från inlägget.</p>
          <div className="blog-cta-actions">
            <Link to="/produkter" className="blog-cta-primary">
              Handla direkt i webbshoppen
            </Link>
            <a href="mailto:butik@lillaflora.nu" className="blog-cta-secondary">
              Mejla floristteamet
            </a>
          </div>
        </div>
      </article>

      {!!suggestions.length && (
        <section className="blog-related container">
          <div className="blog-related-header">
            <p className="blog-tag">Läs vidare</p>
            <h2>Mer mys från Floristbloggen</h2>
          </div>
          <div className="blog-related-grid">
            {suggestions.map((item) => (
              <article className="blog-card" key={item.id}>
                {item.hero_image ? (
                  <img src={item.hero_image} alt={item.title} loading="lazy" />
                ) : (
                  <div className="blog-card-image placeholder" aria-hidden="true">
                    <span>Inspiration</span>
                  </div>
                )}
                <div className="blog-card-content">
                  <h3>{item.title}</h3>
                  <p>{item.short_description}</p>
                  <div className="blog-meta">
                    <span>{formatDate(item.published_at || item.created_at)}</span>
                    <span>{estimateReadingTime(item.content || '')} min</span>
                  </div>
                  <Link to={`/floristbloggen/${item.slug}`} className="blog-link">
                    Läs artikeln →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default FloristBlogPost;
